"use client";

/**
 * <ConsentModal> — 동의 카테고리별 세부 toggle 모달 (Phase 0).
 *
 * 카테고리:
 *   - 필수 (necessary): 항상 ON, toggle 비활성
 *   - 분석 (analytics): 행동 추적 · 추천 정확도 개선
 *   - 마케팅 (marketing): 프로모션 이메일 · 푸시 (V0 에는 미사용 — 미래 대비)
 *
 * 진입:
 *   1. ConsentBanner 의 "세부 설정" 버튼
 *   2. mypage / footer 의 "동의 다시 설정" 링크 (재방문 시)
 */

import { useState, useEffect } from "react";
import { X, Lock } from "lucide-react";
import { useConsent } from "@/contexts/consent-context";

export function ConsentModal({biteSurface=false}:{biteSurface?:boolean}) {
  const { consent, advertising: adConsent, modalOpen, closeModal, update } = useConsent();
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);
  const [saving, setSaving] = useState(false);
  const [advertising, setAdvertising] = useState(adConsent?.enabled || false);
  const [error, setError] = useState("");

  // modal 열릴 때마다 현재 값으로 reset
  useEffect(() => {
    if (modalOpen) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
      setAdvertising(adConsent?.enabled || false);
      setError("");
    }
  }, [modalOpen, consent, adConsent]);

  // Esc 로 닫기
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  if (!modalOpen) return null;

  const onSave = async () => {
    setSaving(true);
    try {
      setError("");
      await update({ analytics, marketing, ...(adConsent?.available ? { advertising } : {}) });
      closeModal();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "동의를 저장하지 못했습니다. 다시 시도해주세요.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bs-consent-overlay" role="dialog" aria-modal="true" aria-label="동의 세부 설정">
      <div className="bs-consent-overlay-bg" onClick={closeModal} />
      <div className="bs-consent-modal">
        <header className="bs-consent-modal-head">
          <h2>동의 세부 설정</h2>
          <button
            type="button"
            className="bs-consent-modal-close"
            onClick={closeModal}
            aria-label="닫기"
          >
            <X size={16} />
          </button>
        </header>

        <div className="bs-consent-modal-body">
          {/* 필수 — 비활성 toggle (항상 ON) */}
          <ConsentRow
            title="필수"
            description="로그인과 결제 및 보안에 필요한 정보를 처리합니다. 필수 정보가 없으면 해당 기능을 이용할 수 없습니다."
            checked={true}
            disabled={true}
            onChange={() => undefined}
            locked
          />

          {/* 분석 */}
          <ConsentRow
            title="분석"
            description={biteSurface?"동의하면 방문 식별자로 페이지와 클릭 및 구매 경로를 연결하고, 로그인 시 계정과 이메일에 연결해 관리자만 확인합니다. Meta 광고 측정과는 별도로 처리합니다. 동의하지 않으면 방문과 세션 및 계정 연결키를 제외하고 날짜별 이용 횟수와 결제 합계만 집계합니다. 서비스 이용에는 영향이 없습니다.":"페이지 체류 시간과 클릭 등 서비스 이용 기록을 분석해 서비스 품질을 개선합니다. 동의하지 않아도 서비스를 이용할 수 있습니다."}
            checked={analytics}
            onChange={setAnalytics}
          />

          {/* 마케팅 */}
          <ConsentRow
            title="마케팅"
            description="이메일과 푸시 알림으로 신기능과 프로모션을 안내합니다. 언제든 수신 동의를 철회할 수 있습니다."
            checked={marketing}
            onChange={setMarketing}
          />
          {adConsent?.available && <ConsentRow
            title="광고 성과 측정 (선택)"
            description="동의하면 광고 성과 측정과 광고 최적화를 위해 Meta Platforms, Inc.에 광고 클릭 식별자, IP 주소, 브라우저 정보, 구매 시각, 결제 금액과 통화 및 중복 방지 식별자를 전송합니다. 이메일과 이름, 분석한 종목 및 보고서 내용은 보내지 않습니다. Bite는 동의 기록을 90일, 결제 연결 정보를 최대 30일, 전송 대기 정보를 최대 7일 보관합니다. Meta의 처리는 Meta 개인정보처리방침을 따릅니다. 동의하지 않아도 구매할 수 있으며 아래 개인정보 설정에서 언제든 철회하면 이후 전송을 중단합니다."
            checked={advertising}
            onChange={setAdvertising}
            disabled={saving}
          />}
          {adConsent?.available && <p className="bs-consent-row-desc"><a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta 개인정보처리방침 확인하기</a></p>}
          {error && <p className="bs-consent-row-desc" role="alert">{error}</p>}
        </div>

        <footer className="bs-consent-modal-foot">
          <button
            type="button"
            className="bs-consent-btn bs-consent-btn-ghost"
            onClick={closeModal}
            disabled={saving}
          >
            취소
          </button>
          <button
            type="button"
            className="bs-consent-btn bs-consent-btn-primary"
            onClick={() => { void onSave(); }}
            disabled={saving}
          >
            {saving ? "저장 중…" : "저장"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConsentRow({
  title, description, checked, onChange, disabled, locked,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  locked?: boolean;
}) {
  return (
    <div className={`bs-consent-row ${disabled ? "is-disabled" : ""}`}>
      <div className="bs-consent-row-text">
        <div className="bs-consent-row-title">
          {title}
          {locked && <Lock size={12} className="bs-consent-row-lock" aria-label="필수" />}
        </div>
        <p className="bs-consent-row-desc">{description}</p>
      </div>
      <label className="bs-consent-toggle">
        <input
          type="checkbox"
          aria-label={title}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        <span className="bs-consent-toggle-track">
          <span className="bs-consent-toggle-thumb" />
        </span>
      </label>
    </div>
  );
}
